import { bucketIndexFor, emptyBuckets, ordersBetween } from './analytics';
import type { Bucket, DateRange, Period } from './analytics';
import type { Expense, ExpenseCategory, Order } from '../types';

/**
 * Money out, over the same four periods as the Analytics screen.
 *
 * Everything here mirrors `analytics.ts` deliberately: the same period ranges,
 * the same bucket rules, the same "floored at 1" guard on bar maxima. The two
 * screens sit under identical period tabs, and a figure that disagreed between
 * them by a bucket boundary would be worse than no figure at all.
 */

export interface ExpenseSummary {
  /** Number of entries recorded in the period. */
  entries: number;
  /** Rupees paid out. */
  total: number;
}

/**
 * Revenue against spending for one period. The reason the screen exists: an
 * owner wants the one number at the bottom, not two lists to subtract by hand.
 */
export interface ProfitSummary {
  revenue: number;
  spent: number;
  /** Revenue minus spending. Negative is a real answer, not an error. */
  net: number;
  /**
   * Net as a percent of revenue, or null when nothing was sold — a margin on
   * zero revenue is not 0%, it is undefined, and showing "0%" on a closed day
   * would read as breaking even.
   */
  marginPct: number | null;
}

export interface ExpenseHeadTotal {
  /** Undefined when the head was deleted after the money was spent. */
  cat: ExpenseCategory | undefined;
  /** Falls back to the raw id, or '' for an uncategorised entry. */
  catId: string;
  total: number;
  entries: number;
}

/** Entries whose payment date falls in [from, to). */
export function expensesBetween(list: Expense[], from: number, to: number): Expense[] {
  return list.filter((e) => e.paidAt >= from && e.paidAt < to);
}

export function summariseExpenses(list: Expense[]): ExpenseSummary {
  return {
    entries: list.length,
    total: list.reduce((s, e) => s + e.amount, 0),
  };
}

/**
 * What the period actually made.
 *
 * Orders and expenses are passed already scoped to the same window by the
 * caller, so this cannot silently compare a month of sales against a week of
 * costs.
 */
export function profitFor(orders: Order[], list: Expense[]): ProfitSummary {
  const revenue = orders.reduce((s, o) => s + o.total, 0);
  const spent = list.reduce((s, e) => s + e.amount, 0);
  const net = revenue - spent;
  return {
    revenue,
    spent,
    net,
    marginPct: revenue > 0 ? (net / revenue) * 100 : null,
  };
}

/**
 * Spending per head, largest first.
 *
 * Heads with nothing spent against them are dropped: unlike a chart bucket, an
 * empty row here is noise rather than information, and a shop with twenty
 * heads would push the three that matter off the screen.
 */
export function expenseTotalsByHead(
  list: Expense[],
  categories: ExpenseCategory[],
): ExpenseHeadTotal[] {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const totals = new Map<string, ExpenseHeadTotal>();

  for (const e of list) {
    // A deleted head leaves its entries behind on purpose — the money was
    // still spent. They group together under one uncategorised row.
    const key = e.categoryId ?? '';
    const found = totals.get(key);
    if (found) {
      found.total += e.amount;
      found.entries += 1;
    } else {
      totals.set(key, {
        cat: byId.get(key),
        catId: key,
        total: e.amount,
        entries: 1,
      });
    }
  }

  return [...totals.values()].sort((a, b) => b.total - a.total);
}

/** Largest head total, floored at 1 so a bar never divides by zero. */
export function maxHeadTotal(totals: ExpenseHeadTotal[]): number {
  return Math.max(1, ...totals.map((t) => t.total));
}

/**
 * Spending over time, in the same buckets the revenue chart uses.
 *
 * `Bucket` is the order-shaped record the chart component already draws, so
 * spending is carried in its `revenue` field and the count of entries in
 * `orders`. That is a reuse of the shape, not a claim about what the numbers
 * are — the chart is handed its own labels by the caller.
 */
export function buildExpenseSeries(
  list: Expense[],
  period: Period,
  now: Date = new Date(),
  custom?: DateRange,
): Bucket[] {
  const buckets = emptyBuckets(period, now, custom);

  for (const e of list) {
    const idx = bucketIndexFor(e.paidAt, buckets);
    if (idx < 0) continue;
    buckets[idx].revenue += e.amount;
    buckets[idx].orders += 1;
  }

  return buckets;
}

/**
 * Both sides of one period, scoped together.
 *
 * A convenience over calling `ordersBetween` and `expensesBetween` separately,
 * so a screen cannot scope one of them and forget the other.
 */
export function scopeToPeriod(
  orders: Order[],
  list: Expense[],
  from: number,
  to: number,
): { orders: Order[]; expenses: Expense[] } {
  return {
    orders: ordersBetween(orders, from, to),
    expenses: expensesBetween(list, from, to),
  };
}

/** Newest payment first — the order the entry list is read in. */
export function sortExpenses(list: Expense[]): Expense[] {
  return [...list].sort((a, b) => b.paidAt - a.paidAt);
}
